import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Download, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const TABS = [
  { v: "listings", l: "Listings", cols: ["created_at", "owner_name", "owner_email", "owner_phone", "property_type", "location", "price", "title_status", "photos"] },
  { v: "landowner", l: "Landowner", cols: ["created_at", "name", "email", "phone", "location", "acreage", "title_status", "model"] },
  { v: "investor", l: "Investor", cols: ["created_at", "name", "email", "phone", "ticket_size", "interest"] },
  { v: "nri", l: "NRI", cols: ["created_at", "name", "email", "country_code", "phone", "country", "service", "whatsapp_preferred"] },
  { v: "viewings", l: "Viewings", cols: ["created_at", "property_title", "name", "email", "phone", "preferred_date"] },
  { v: "contacts", l: "Contacts", cols: ["created_at", "name", "email", "phone", "subject", "message"] },
  { v: "users", l: "Users", cols: ["created_at", "name", "email", "role"] },
];

function fmtCell(v, key) {
  if (key === "photos" && Array.isArray(v)) {
    if (!v.length) return <span className="text-[#EDE6D3]/40">—</span>;
    return (
      <div className="flex gap-1.5">
        {v.slice(0, 4).map((src, i) => (
          <a key={i} href={src} target="_blank" rel="noreferrer" className="block w-10 h-10 border border-[#EDE6D3]/15 overflow-hidden hover:border-[#D4AF37] transition-colors">
            <img src={src} alt="" className="w-full h-full object-cover" />
          </a>
        ))}
        {v.length > 4 && <span className="text-[10px] text-[#EDE6D3]/50 self-center">+{v.length - 4}</span>}
      </div>
    );
  }
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  return String(v);
}

export default function Admin() {
  const { user, ready } = useAuth();
  const [summary, setSummary] = useState({});
  const [active, setActive] = useState("listings");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [analytics, setAnalytics] = useState({ chart: [], totals: {} });

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/summary").then((r) => setSummary(r.data)).catch(() => {});
      api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    setLoading(true);
    api.get(`/admin/${active}`).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [active, user]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/${active}/export`, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const fname = match ? match[1] : `bbe-${active}.csv`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} ${active} rows.`);
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  if (!ready) return <div className="pt-[200px] px-10"><div className="eyebrow">Loading…</div></div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin") return (
    <div className="pt-[172px] max-w-[1440px] mx-auto px-6 md:px-10 min-h-[70vh]">
      <div className="eyebrow">403 · Restricted</div>
      <h1 className="mt-4 font-serif text-[54px] leading-tight">This desk is for staff only.</h1>
      <p className="mt-6 text-[14px] text-[#EDE6D3]/70">Contact Build Brigade Enterprises to be issued an operator account.</p>
    </div>
  );

  const active_cfg = TABS.find((t) => t.v === active);

  return (
    <div className="pt-[112px]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="eyebrow mb-6">Admin Console · Operators Only</div>
        <h1 className="font-serif text-[54px] md:text-[80px] leading-[0.95] tracking-tight" data-testid="admin-title">
          <MaskedLines lines={["Every submission,", "on one desk."]} />
        </h1>
        <p className="mt-8 max-w-2xl text-[14px] text-[#EDE6D3]/70">Signed in as <span className="text-[#D4AF37]">{user.name}</span> · {user.email}</p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {TABS.map((t, i) => (
          <Reveal key={t.v} delay={i * 0.04}>
            <button
              onClick={() => setActive(t.v)}
              data-testid={`admin-summary-${t.v}`}
              className={`w-full text-left border p-5 transition-colors ${active === t.v ? "bg-[#EDE6D3] text-[#0A2342] border-[#EDE6D3]" : "card-sand hover:border-[#D4AF37]"}`}
            >
              <div className={`eyebrow ${active === t.v ? "text-[#D4AF37]" : ""}`}>{t.l}</div>
              <div className="mt-3 font-serif text-[36px] leading-none">{summary[t.v] ?? 0}</div>
            </button>
          </Reveal>
        ))}
      </section>

      {/* Lead Analytics Chart */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10" data-testid="admin-analytics">
        <Reveal>
          <div className="card-sand p-8 md:p-10">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2 eyebrow mb-2"><BarChart3 size={14} className="text-[#D4AF37]" /> Lead Analytics</div>
                <h3 className="font-serif text-[26px] md:text-[32px] leading-tight">Enquiry Volume by Week</h3>
              </div>
              <div className="flex gap-4 flex-wrap">
                {[
                  { key: "contacts", label: "Contacts", color: "#D4AF37" },
                  { key: "landowner", label: "Landowner", color: "#6E8A5F" },
                  { key: "investor", label: "Investor", color: "#E7CB6A" },
                  { key: "nri", label: "NRI", color: "#B8952E" },
                  { key: "viewings", label: "Viewings", color: "#C4602A" },
                ].map((s) => (
                  <div key={s.key} className="flex items-center gap-2 text-[11px] tracking-[1px] uppercase text-[#EDE6D3]/70">
                    <span className="w-3 h-3" style={{ background: s.color }} />
                    {s.label}: <span className="text-[#EDE6D3] font-medium">{analytics.totals?.[s.key] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
            {analytics.chart.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={analytics.chart} barCategoryGap="20%">
                  <XAxis dataKey="week" tick={{ fill: "#EDE6D3", fontSize: 10, letterSpacing: "1px" }} axisLine={{ stroke: "#EDE6D3", strokeOpacity: 0.15 }} tickLine={false} />
                  <YAxis tick={{ fill: "#EDE6D3", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#0E2A4E", border: "1px solid rgba(237,230,211,0.15)", color: "#EDE6D3", fontSize: "12px" }}
                    cursor={{ fill: "rgba(212,175,55,0.08)" }}
                  />
                  <Bar dataKey="contacts" stackId="a" fill="#D4AF37" name="Contacts" />
                  <Bar dataKey="landowner" stackId="a" fill="#6E8A5F" name="Landowner" />
                  <Bar dataKey="investor" stackId="a" fill="#E7CB6A" name="Investor" />
                  <Bar dataKey="nri" stackId="a" fill="#B8952E" name="NRI" />
                  <Bar dataKey="viewings" stackId="a" fill="#C4602A" name="Viewings" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[320px] flex items-center justify-center text-[#EDE6D3]/40 text-[13px]">
                No enquiry data yet. Leads will appear here as they come in.
              </div>
            )}
          </div>
        </Reveal>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-14 pb-8" data-testid="admin-table-section">
        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="w-full flex bg-transparent border-b border-[#EDE6D3]/10 rounded-none p-0 h-auto justify-start gap-0 overflow-x-auto">
            {TABS.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`}
                className="rounded-none py-4 px-5 text-[11px] tracking-[2px] uppercase data-[state=active]:bg-[#EDE6D3] data-[state=active]:text-[#0A2342] data-[state=active]:shadow-none">
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={active} className="pt-8">
            <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
              <div className="text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/60">
                {loading ? "Loading…" : `${rows.length} ${active} record${rows.length === 1 ? "" : "s"}`}
              </div>
              <button
                onClick={exportCsv}
                disabled={exporting || loading}
                className="btn-outline disabled:opacity-50"
                data-testid={`admin-export-${active}`}
              >
                <Download size={14} /> {exporting ? "Exporting…" : "Download CSV"}
              </button>
            </div>
            <div className="card-sand overflow-x-auto" data-testid={`admin-table-${active}`}>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-[#EDE6D3]/20 text-left">
                    {active_cfg.cols.map((c) => (
                      <th key={c} className="py-4 px-4 eyebrow text-[#EDE6D3]/60 whitespace-nowrap">{c.replace(/_/g, " ")}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className="p-6 text-[#EDE6D3]/50" colSpan={active_cfg.cols.length}>Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td className="p-6 text-[#EDE6D3]/50" colSpan={active_cfg.cols.length}>No records yet.</td></tr>
                  ) : rows.map((r, i) => (
                    <tr key={r.id || i} className="border-b border-[#EDE6D3]/10 hover:bg-[#0A2342]/60" data-testid={`admin-row-${active}-${i}`}>
                      {active_cfg.cols.map((c) => (
                        <td key={c} className="py-4 px-4 align-top text-[#EDE6D3]/85 max-w-[240px] truncate" title={typeof r[c] === "string" ? r[c] : ""}>{fmtCell(r[c], c)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
